/**
 * RestoreModal — Batch #14.338 : restauration en 2 étapes (mot de passe → aperçu du contenu → taper RESTAURER).
 * Une sauvegarde de sécurité est créée automatiquement avant de remplacer les données.
 */
import { useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { AlertTriangle, Loader2, X } from "lucide-react";

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300";

export default function RestoreModal({ item, onClose, onDone, scope = "full" }) {
  const base = scope === "dossier" ? "/admin/backup/dossier" : "/admin/backup";
  const [password, setPassword] = useState("");
  const [manifest, setManifest] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const inspect = async () => {
    setBusy(true);
    try { const r = await api.post(`${base}/inspect`, { file_id: item.file_id, password }); setManifest(r.data.manifest); }
    catch (e) { toast.error(e.response?.data?.detail || "Lecture impossible"); }
    finally { setBusy(false); }
  };
  const restore = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${base}/restore`, { file_id: item.file_id, password, confirm }, { timeout: 300000 });
      toast.success(`Restauration terminée : ${r.data.collections} collections, ${r.data.documents} documents. Sauvegarde de sécurité : ${r.data.safety_backup}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Restauration impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="backup-restore-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-slate-800">{scope === "dossier" ? "Restaurer un dossier" : "Restaurer une sauvegarde"}</h3>
            {scope === "dossier" && manifest?.scope?.name && <div className="text-xs text-indigo-700" data-testid="backup-restore-scope">Dossier : {manifest.scope.name}{manifest.scope.client_number ? ` · n° ${manifest.scope.client_number}` : ""} — seules ses données seront remplacées</div>}
            <div className="text-xs font-mono text-slate-500">{item.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="backup-restore-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div><b>Toutes les données actuelles seront remplacées</b> par celles de cette sauvegarde (comptabilité, paie, dossiers, fiscal, fichiers). Une sauvegarde de sécurité de l'état actuel est faite automatiquement juste avant, dans le Drive.</div>
        </div>
        {!manifest ? (
          <div className="space-y-2">
            <label className="text-xs text-slate-600">Mot de passe de l'archive
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoFocus data-testid="backup-restore-password" />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annuler</button>
              <button onClick={inspect} disabled={busy || !password} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50" data-testid="backup-restore-inspect">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Vérifier le contenu</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-slate-700 bg-slate-50 border rounded-lg p-3" data-testid="backup-restore-manifest">
              Sauvegarde du <b>{new Date(manifest.created_at).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" })}</b> · {Object.keys(manifest.collections || {}).length} collections · <b>{manifest.total_documents}</b> documents · {manifest.files?.gridfs ?? 0} fichier(s)
            </div>
            <label className="text-xs text-slate-600">Tapez <b>RESTAURER</b> pour confirmer
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} placeholder="RESTAURER" data-testid="backup-restore-confirm" />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annuler</button>
              <button onClick={restore} disabled={busy || confirm.trim().toUpperCase() !== "RESTAURER"} className="bg-rose-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-rose-700 disabled:opacity-50" data-testid="backup-restore-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {busy ? "Restauration en cours…" : "Restaurer maintenant"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
